import api from "./axios";
import { fetchOwnerProfile } from "./ownerCarsApi";

/* ───────── Helpers ───────── */

function normalizePhoneForApi(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length <= 10) return digits;
  return digits.slice(-10);
}

function normalizeEmailForApi(value) {
  const v = String(value || "").trim();
  return v || null;
}

function normalizeLicenseForApi(value) {
  return String(value || "").trim().toUpperCase();
}

function normalizeNameForApi(value) {
  return String(value || "").trim();
}

function getErrorMessage(err, fallback = "Request failed.") {
  const raw = err?.response?.data;
  return (
    (typeof raw === "string" && raw.trim()) ||
    raw?.message ||
    raw?.error ||
    raw?.detail ||
    err?.message ||
    fallback
  );
}

function isAlreadyLinkedLikeError(err) {
  const status = err?.response?.status;
  const msg = String(getErrorMessage(err, "")).toLowerCase();

  return (
    status === 409 ||
    msg.includes("already linked") ||
    msg.includes("already assigned") ||
    msg.includes("already mapped") ||
    msg.includes("already attached") ||
    msg.includes("already belongs")
  );
}

function buildSafeDriverPayload(payload) {
  const name = normalizeNameForApi(payload?.name);
  const phoneNumber = normalizePhoneForApi(payload?.phoneNumber);
  const email = normalizeEmailForApi(payload?.email);
  const licenseNumber = normalizeLicenseForApi(payload?.licenseNumber);

  if (!name) throw new Error("name is required");
  if (phoneNumber.length !== 10) {
    throw new Error("phoneNumber must resolve to a valid 10-digit number");
  }
  if (!licenseNumber) throw new Error("licenseNumber is required");

  return {
    name,
    phoneNumber,
    email,
    licenseNumber,
  };
}

async function resolveOwnerId(ownerId) {
  let effectiveOwnerId = ownerId;

  if (
    !effectiveOwnerId ||
    effectiveOwnerId === "current-owner" ||
    effectiveOwnerId === "current"
  ) {
    const profile = await fetchOwnerProfile();
    effectiveOwnerId = profile?.id;
  }

  if (!effectiveOwnerId) {
    throw new Error("Unable to resolve current owner id from profile");
  }

  return effectiveOwnerId;
}

/* ───────── Driver APIs ───────── */

export async function fetchOwnerDrivers(ownerId) {
  const effectiveOwnerId = await resolveOwnerId(ownerId);
  const { data } = await api.get(`/drivers/by-owner/${effectiveOwnerId}`);
  return Array.isArray(data) ? data : [];
}

export async function unlinkDriverFromOwner(driverId) {
  if (!driverId) throw new Error("driverId is required");
  await api.put(`/drivers/unlink-from-owner/${driverId}`);
}

export async function linkExistingDriverToOwner(ownerId, phoneNumber) {
  const effectiveOwnerId = await resolveOwnerId(ownerId);

  const normalizedPhone = normalizePhoneForApi(phoneNumber);
  if (normalizedPhone.length !== 10) {
    throw new Error("phoneNumber must resolve to a valid 10-digit number");
  }

  const { data } = await api.put(
    `/drivers/assign-to-owner?ownerId=${encodeURIComponent(
      effectiveOwnerId
    )}&phoneNumber=${encodeURIComponent(normalizedPhone)}`
  );

  return data;
}

/**
 * Correct endpoint for owner-side add:
 * POST /drivers/add/{ownerId}
 *
 * This creates and links in one backend operation.
 */
export async function addDriverForOwner(ownerId, payload) {
  const effectiveOwnerId = await resolveOwnerId(ownerId);
  const safe = buildSafeDriverPayload(payload);

  const { data } = await api.post(`/drivers/add/${effectiveOwnerId}`, safe);
  return data;
}

/**
 * Keep this exported name for compatibility with existing screen imports,
 * but internally route to the correct backend endpoint.
 */
export async function registerDriverAndLinkToOwner(ownerId, payload) {
  return addDriverForOwner(ownerId, payload);
}

export async function uploadDriverDocument(driverId, file, docType) {
  if (!driverId || !file) return null;

  const form = new FormData();
  form.append("file", file);

  const typeParam = encodeURIComponent(docType || "OTHER");

  const { data } = await api.post(
    `/drivers/${driverId}/documents?type=${typeParam}`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  return data;
}

export async function uploadDriverPhoto(driverId, file, slot) {
  if (!driverId || !file) return null;

  const form = new FormData();
  form.append("file", file);

  const slotParam = encodeURIComponent(slot || "EXTRA");

  const { data } = await api.post(
    `/drivers/${driverId}/photos?slot=${slotParam}`,
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );

  return data;
}